import { useState, useEffect, useCallback } from 'react';
import {
  type TabPersistenceConfig,
  saveSelectedTab,
  getSavedTab,
} from './tabPersistence';

export interface UseTabPersistenceReturn {
  selectedTab: string | undefined;
  setSelectedTab: (tab: string) => void;
  isReady: boolean;
}

export const useTabPersistence = (
  config: TabPersistenceConfig,
  availableTabs?: string[]
): UseTabPersistenceReturn => {
  const [selectedTab, setSelectedTabState] = useState<string | undefined>(
    undefined
  );
  const [isReady, setIsReady] = useState(false);

  const getValidatedTab = useCallback(() => {
    const savedTab = getSavedTab(config);

    if (availableTabs && savedTab && !availableTabs.includes(savedTab)) {
      const defaultTab =
        config.defaultTab && availableTabs.includes(config.defaultTab)
          ? config.defaultTab
          : availableTabs[0];
      return defaultTab;
    }

    return savedTab;
  }, [config, availableTabs]);

  useEffect(() => {
    const validatedTab = getValidatedTab();
    setSelectedTabState(validatedTab);
    setIsReady(true);
  }, [getValidatedTab]);

  useEffect(() => {
    const handlePopState = () => {
      const validatedTab = getValidatedTab();
      setSelectedTabState(validatedTab);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [getValidatedTab]);

  const setSelectedTab = useCallback(
    (tab: string) => {
      if (availableTabs && !availableTabs.includes(tab)) {
        console.warn(
          `Tab "${tab}" is not in the list of available tabs:`,
          availableTabs
        );
        return;
      }

      setSelectedTabState(tab);
      saveSelectedTab(config, tab);
    },
    [config, availableTabs]
  );

  return {
    selectedTab,
    setSelectedTab,
    isReady,
  };
};
